import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { SessionNotes } from 'services/http';
import { isEmpty, first, truncate, isUndefined } from 'lodash';

import { Box, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { NotePopover, NoteDialog, NoteDrawer } from './components';
import isSessionAnonymised from 'utils/isSessionAnonymised';
import Notifier from 'components/Notifier/Notifier';
import notesIcon from '@images/notes-icon.svg';

const useStyles = makeStyles(() => ({
  root: {},
  popover: {
    marginTop: 40,
    borderRadius: '4px',
  },
  popoverContainer: {
    width: 380,
    height: 220,
    lineHeight: '18px',
    padding: 10,
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
  },
}));

const Notes = ({ sessionDetails, reFetch, showDrawer, setShowDrawer }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [notes, setNotes] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [anonSession, setAnonSession] = useState(null);

  useEffect(() => {
    const isAnonymised = isSessionAnonymised(sessionDetails?.notes?.[0]?.note);
    setAnonSession(isAnonymised);
  }, [sessionDetails.notes]);

  const isPopperOpen = Boolean(anchorEl);
  const togglePopover = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const showLastNote = (notes) => {
    if (!isEmpty(notes)) {
      const lastNote = first(notes);
      return truncate(lastNote.note, { length: 15 });
    }
    return '-';
  };
  const getNotes = useCallback(() => {
    SessionNotes.getSessionNotes({ sessionId: sessionDetails.id })
      .then((data) => {
        if (data.data?.[0]) {
          isSessionAnonymised(data.data?.[0]?.note)
            ? setAnonSession(true)
            : setAnonSession(false);
        }
        setNotes(data.data);
      })
      .catch((err) => console.error(err));
  }, [sessionDetails.id]);

  const handleSubmitNote = useCallback(
    (note) => {
      const modifiedData = { note: note };
      SessionNotes.createNote({
        sessionId: sessionDetails.id,
        data: modifiedData,
      })
        .then(() => {
          setOpenDialog(false);
          if (sessionDetails.notes) {
            Notifier.success(t('success'));
            reFetch();
          } else {
            getNotes();
          }
        })
        .catch((err) => console.error(err));
    },
    [sessionDetails, t, reFetch, getNotes],
  );

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      {sessionDetails.notes && (
        <>
          {showLastNote(sessionDetails.notes)}
          <IconButton style={{ padding: 5 }} onClick={togglePopover}>
            <img alt={'Logo'} src={notesIcon} style={{ padding: 0 }} />
          </IconButton>
        </>
      )}
      {!isUndefined(sessionDetails.notes) && (
        <NotePopover
          anonymisedSession={anonSession}
          togglePopover={togglePopover}
          anchorEl={anchorEl}
          open={isPopperOpen}
          data={sessionDetails}
          setOpenDialog={setOpenDialog}
          popoverClassName={classes.popover}
          popoverContainerClassName={classes.popoverContainer}
          headerClassName={classes.popoverHeader}
          title="session_notes"
          headerButtonTitle="add_note"
        />
      )}
      <NoteDialog
        open={openDialog}
        onCloseModal={setOpenDialog}
        title="add_a_note"
        descr="note_dialog_descr"
        secDescr="order_number"
        data={sessionDetails}
        onSubmitNote={handleSubmitNote}
        divider={true}
      />
      {isUndefined(sessionDetails.notes) && (
        <NoteDrawer
          title={t('session_notes')}
          setOpenDialog={setOpenDialog}
          anonymisedSession={anonSession}
          sessionDetails={sessionDetails}
          showDrawer={showDrawer}
          setShowDrawer={setShowDrawer}
          getNotes={getNotes}
          notes={notes}
        />
      )}
    </Box>
  );
};

export default Notes;
